import React, { useState, useEffect } from 'react';
import { BarChart3, Clock, DollarSign, Users, CheckCircle, AlertTriangle, Target, TrendingUp, Briefcase, Award, Plus, MessageSquare, Calendar, Send, CheckCircle2, X, PenTool } from 'lucide-react';
import toast from 'react-hot-toast';
import { getTmOwnerDashboard, getTmSupervisorDashboard, getTmTeamLeaderDashboard, getTmTeamMemberDashboard, getTmSupervisorReminders, createTmTouchpoint, TM_STATUS_MAP, TM_STATUS_COLORS, formatIQD, formatSeconds } from '../../utils/tm_api';

export default function TmDashboardPage() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = ['owner', 'admin'].includes(user.role);
  const isSupervisor = user.role === 'supervisor';
  const isTeamLeader = user.role === 'team_leader';
  const isTeamMember = user.role === 'team_member';

  if (isOwner) return <OwnerDashboard />;
  if (isSupervisor) return <SupervisorDashboard />;
  if (isTeamLeader) return <TeamLeaderDashboard />;
  if (isTeamMember) return <TeamMemberDashboard />;

  return <div className="p-6 text-center text-[var(--text-muted)]" dir="rtl">غير متاح</div>;
}

function OwnerDashboard() {
  const now = new Date();
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getTmOwnerDashboard(selectedMonth)
      .then(setData)
      .catch(e => toast.error('خطأ في تحميل البيانات'))
      .finally(() => setLoading(false));
  }, [selectedMonth]);

  if (loading) return <div className="p-6 text-center text-[var(--text-muted)]" dir="rtl">جاري التحميل...</div>;
  if (!data) return null;

  const ts = data.task_stats || {};
  const activeTeams = data.team_performance || [];

  const getMonthsList = () => {
    const list = [];
    const d = new Date();
    for (let i = 0; i < 6; i++) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const label = d.toLocaleDateString('ar-IQ', { month: 'long', year: 'numeric' });
      list.push({ value: `${year}-${month}`, label });
      d.setMonth(d.getMonth() - 1);
    }
    return list;
  };
  const months = getMonthsList();
  const selectedTeam = activeTeams.find(t => String(t.id) === selectedTeamId);

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-blue-500 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[var(--text-dark-h)]">لوحة التحكم — المالك</h1>
          <p className="text-sm text-[var(--text-muted)]">الشهر: {data.current_month}</p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="مهام مقبولة" value={ts.accepted || 0} subtitle={`من ${ts.total || 0} إجمالي`} icon={<CheckCircle className="w-5 h-5" />} color="green" />
        <StatCard title="ثواني هذا الشهر" value={formatSeconds(data.monthly_seconds)} icon={<Clock className="w-5 h-5" />} color="blue" />
        <StatCard title="أرباح الشهر" value={formatIQD(data.earnings_this_month)} icon={<DollarSign className="w-5 h-5" />} color="purple" />
        <StatCard title="غير مدفوع" value={formatIQD(data.unpaid_earnings)} icon={<TrendingUp className="w-5 h-5" />} color="red" />
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {['draft', 'assigned', 'in_progress', 'submitted', 'tl_approved', 'revision'].map(key => (
          <div key={key} className="text-center p-3 rounded-xl bg-[var(--card-bg)] border border-[var(--card-border)]">
            <p className="text-lg font-bold text-[var(--text-dark-h)]">{ts[key] || 0}</p>
            <p className="text-[10px] text-[var(--text-muted)]">{TM_STATUS_MAP[key.toUpperCase()]?.label || key}</p>
          </div>
        ))}
      </div>

      <DelayedTasksPanel tasks={data.delayed_tasks} />

      {/* Team Performance */}
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <h3 className="font-bold text-sm text-[var(--text-dark-h)] flex items-center gap-1.5">
            <Award className="w-4 h-4 text-yellow-500" /> 
            <span>أداء الفرق</span>
          </h3>
          
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 bg-[var(--bg-flat-50)] rounded-xl border border-[var(--border-light)] px-2.5 py-1">
              <Calendar className="w-3.5 h-3.5 text-[var(--text-gray)]" />
              <select 
                value={selectedMonth} 
                onChange={e => setSelectedMonth(e.target.value)}
                className="bg-transparent border-0 text-xs font-bold text-[var(--text-dark-h)] focus:ring-0 outline-none cursor-pointer pr-6 py-0.5"
              >
                {months.map(m => <option key={m.value} value={m.value} className="bg-[var(--card-bg)] text-[var(--text-dark-h)]">{m.label}</option>)}
              </select>
            </div>

            <div className="flex items-center gap-1 bg-[var(--bg-flat-50)] rounded-xl border border-[var(--border-light)] px-2.5 py-1">
              <Users className="w-3.5 h-3.5 text-[var(--text-gray)]" />
              <select 
                value={selectedTeamId} 
                onChange={e => setSelectedTeamId(e.target.value)}
                className="bg-transparent border-0 text-xs font-bold text-[var(--text-dark-h)] focus:ring-0 outline-none cursor-pointer pr-6 py-0.5"
              >
                <option value="" className="bg-[var(--card-bg)] text-[var(--text-dark-h)]">جميع الفرق</option>
                {activeTeams.map(t => <option key={t.id} value={String(t.id)} className="bg-[var(--card-bg)] text-[var(--text-dark-h)]">{t.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {selectedTeamId ? (
          selectedTeam ? (
            <div className="space-y-4 animate-fade-in">
              <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h4 className="text-sm font-extrabold text-[var(--text-dark-h)]">تقرير أداء فريق: {selectedTeam.name}</h4>
                  <p className="text-xs text-[var(--text-gray)]">التقرير للفترة المحددة: {months.find(m => m.value === selectedMonth)?.label || selectedMonth}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold text-[var(--text-muted)]">نسبة إنجاز التارجت</span>
                  <p className="text-lg font-black text-blue-500">
                    {selectedTeam.monthly_target_seconds > 0 
                      ? Math.round((selectedTeam.total_seconds / selectedTeam.monthly_target_seconds) * 100) 
                      : 0}%
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-2xl border border-[var(--card-border)] bg-[var(--bg-flat-50)] flex flex-col justify-between h-28 hover:shadow-sm transition">
                  <div className="flex items-center gap-2 text-[var(--text-gray)]">
                    <Target className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-bold">التقدم والهدف</span>
                  </div>
                  <div>
                    <p className="text-[10px] text-[var(--text-muted)] mt-1">المستهدف: {formatSeconds(selectedTeam.monthly_target_seconds)}</p>
                    <p className="text-sm font-black text-[var(--text-dark-h)]">{formatSeconds(selectedTeam.total_seconds)} مُنجز</p>
                  </div>
                </div>

                <div className="p-4 rounded-2xl border border-[var(--card-border)] bg-[var(--bg-flat-50)] flex flex-col justify-between h-28 hover:shadow-sm transition">
                  <div className="flex items-center gap-2 text-[var(--text-gray)]">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    <span className="text-xs font-bold">أرباح الفريق</span>
                  </div>
                  <div>
                    <p className="text-base font-black text-green-600 dark:text-green-400">{formatIQD(selectedTeam.total_iqd)}</p>
                    <p className="text-[10px] text-[var(--text-muted)]">إجمالي الأرباح المستحقة</p>
                  </div>
                </div>

                <div className="p-4 rounded-2xl border border-[var(--card-border)] bg-[var(--bg-flat-50)] flex flex-col justify-between h-28 hover:shadow-sm transition col-span-2 md:col-span-1">
                  <div className="flex items-center gap-2 text-[var(--text-gray)]">
                    <TrendingUp className="w-4 h-4 text-purple-500" />
                    <span className="text-xs font-bold">حالة المدفوعات</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-green-600 dark:text-green-400 font-bold">المدفوع:</span>
                      <span className="font-semibold text-[var(--text-dark-h)]">{formatIQD(selectedTeam.paid_iqd)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-red-500 font-bold">غير المدفوع:</span>
                      <span className="font-semibold text-[var(--text-dark-h)]">{formatIQD(selectedTeam.unpaid_iqd)}</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl border border-[var(--card-border)] bg-[var(--bg-flat-50)] flex flex-col justify-between h-28 hover:shadow-sm transition">
                  <div className="flex items-center gap-2 text-[var(--text-gray)]">
                    <Clock className="w-4 h-4 text-sky-500" />
                    <span className="text-xs font-bold">إجمالي تسليم الدقائق</span>
                  </div>
                  <div>
                    <p className="text-sm font-black text-[var(--text-dark-h)]">
                      {Math.floor(selectedTeam.total_seconds / 60)} دقيقة و {selectedTeam.total_seconds % 60} ثانية
                    </p>
                    <p className="text-[10px] text-[var(--text-muted)]">المدة الكلية للفيديوهات</p>
                  </div>
                </div>

                <div className="p-4 rounded-2xl border border-[var(--card-border)] bg-[var(--bg-flat-50)] flex flex-col justify-between h-28 hover:shadow-sm transition">
                  <div className="flex items-center gap-2 text-[var(--text-gray)]">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    <span className="text-xs font-bold">المهام المنجزة</span>
                  </div>
                  <div>
                    <p className="text-sm font-black text-[var(--text-dark-h)]">{selectedTeam.task_count || 0} مهمة</p>
                    <p className="text-[10px] text-[var(--text-muted)]">إجمالي المهام المقبولة</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-[var(--text-muted)] text-center py-4">البيانات غير متوفرة لهذا الفريق</p>
          )
        ) : (
          activeTeams.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">لا توجد بيانات</p>
          ) : (
            <div className="space-y-3 animate-fade-in">
              {activeTeams.map(t => {
                const percent = t.monthly_target_seconds > 0 ? Math.min(100, Math.round((t.total_seconds / t.monthly_target_seconds) * 100)) : 0;
                return (
                  <div key={t.id} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-bold text-[var(--text-dark-h)]">{t.name}</span>
                      <span className="text-xs text-[var(--text-muted)]">{formatSeconds(t.total_seconds)} / {formatSeconds(t.monthly_target_seconds)} — {t.task_count} مهمة</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                      <div className={`h-2.5 rounded-full transition-all ${percent >= 100 ? 'bg-green-500' : percent >= 70 ? 'bg-blue-500' : 'bg-yellow-500'}`}
                        style={{ width: `${percent}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Alerts */}
      <div className="grid md:grid-cols-3 gap-4">
        <AlertCard icon={<Users className="w-5 h-5" />} title="فرق نشطة" value={data.team_count} color="blue" />
        <AlertCard icon={<Clock className="w-5 h-5" />} title="طلبات أعضاء بالانتظار" value={data.pending_members} color="yellow" />
        <AlertCard icon={<AlertTriangle className="w-5 h-5" />} title="شكاوى مفتوحة" value={data.open_complaints} color="red" />
      </div>
    </div>
  );
}

function SupervisorDashboard() {
  const now = new Date();
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [data, setData] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Form states for logging touchpoints
  const [activeClient, setActiveClient] = useState(null);
  const [notes, setNotes] = useState('');
  const [contactType, setContactType] = useState('whatsapp');
  const [nextContactDate, setNextContactDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = () => {
    setLoading(true);
    Promise.all([
      getTmSupervisorDashboard(selectedMonth),
      getTmSupervisorReminders().catch(() => [])
    ])
    .then(([dashData, remData]) => {
      setData(dashData);
      setReminders(remData || []);
    })
    .catch(() => toast.error('حدث خطأ أثناء تحميل البيانات'))
    .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [selectedMonth]);

  const getMonthsList = () => {
    const list = [];
    const d = new Date();
    for (let i = 0; i < 6; i++) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const label = d.toLocaleDateString('ar-IQ', { month: 'long', year: 'numeric' });
      list.push({ value: `${year}-${month}`, label });
      d.setMonth(d.getMonth() - 1);
    }
    return list;
  };
  const months = getMonthsList();
  const activeTeams = data?.team_performance || [];
  const selectedTeam = activeTeams.find(t => String(t.id) === selectedTeamId);

  const handleLogTouchpoint = async (e) => {
    e.preventDefault();
    if (!activeClient) return;
    if (!notes.trim()) {
      toast.error('يرجى كتابة ملاحظات التواصل');
      return;
    }

    try {
      setSubmitting(true);
      const todayStr = new Date().toISOString().split('T')[0];
      await createTmTouchpoint(user.employee_id, {
        client_id: activeClient.id,
        contact_date: todayStr,
        contact_type: contactType,
        notes: notes,
        next_contact_date: nextContactDate || null
      });

      toast.success('تم تسجيل تواصل اليوم بنجاح!');
      setActiveClient(null);
      setNotes('');
      setContactType('whatsapp');
      setNextContactDate('');
      loadData(); // reload dashboard stats & touchpoints
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'فشل في تسجيل التواصل');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-6 text-center text-[var(--text-muted)]" dir="rtl">جاري التحميل...</div>;
  if (!data) return null;

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
          <Briefcase className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[var(--text-dark-h)]">لوحة المتابعة والعملاء</h1>
          <p className="text-xs text-[var(--text-muted)]">متابعة العملاء المربوطين، تسجيل التواصل اليومي، ومراقبة أداء فرق العمل</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard title="عملائي المسؤول عنهم" value={data.clients?.length || 0} icon={<Users className="w-5 h-5" />} color="blue" />
        <StatCard title="مهام بانتظار المراجعة" value={data.task_stats?.pending || 0} icon={<Clock className="w-5 h-5" />} color="yellow" />
        <StatCard title="عمولاتي هذا الشهر" value={formatIQD(data.commissions_this_month)} icon={<DollarSign className="w-5 h-5" />} color="green" />
      </div>

      {/* Delayed Tasks Alert Panel */}
      <DelayedTasksPanel tasks={data.delayed_tasks} />

      {/* Alerts & Reminders Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reminders?.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-2xl p-5 space-y-3">
            <h3 className="font-bold text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" /> ⚠️ تنبيه: تواصل متأخر مع العميل (أكثر من 7 أيام)
            </h3>
            <div className="space-y-2">
              {reminders.map((r, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-red-200/50 dark:border-red-900/10 last:border-0 text-sm">
                  <span className="font-bold text-red-800 dark:text-red-300">{r.client_name}</span>
                  <span className="text-[11px] text-red-600 dark:text-red-400">آخر تواصل: {r.last_contact_date || 'غير محدد'} ({r.days_since} يوم)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.upcoming_contacts?.length > 0 && (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 h-fit">
            <h3 className="font-bold text-sm text-[var(--text-dark-h)] mb-3">📅 مواعيد التواصل القادمة المجدولة</h3>
            {data.upcoming_contacts.map((c, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[var(--border-light)] last:border-0 text-sm">
                <span className="text-[var(--text-dark-h)]">{c.client_name}</span>
                <span className="text-xs text-[var(--text-muted)]">موعِد المتابعة: {c.next_contact_date}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Team Performance Panel */}
      {data.team_performance?.length > 0 && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 md:p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <h3 className="font-bold text-sm text-[var(--text-dark-h)] flex items-center gap-2">
              <Award className="w-4.5 h-4.5 text-yellow-500" />
              <span>📊 تقييم أداء الفرق وإنتاجيتها (ثواني ومهام)</span>
            </h3>
            
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 bg-[var(--bg-flat-50)] rounded-xl border border-[var(--border-light)] px-2.5 py-1">
                <Calendar className="w-3.5 h-3.5 text-[var(--text-gray)]" />
                <select 
                  value={selectedMonth} 
                  onChange={e => setSelectedMonth(e.target.value)}
                  className="bg-transparent border-0 text-xs font-bold text-[var(--text-dark-h)] focus:ring-0 outline-none cursor-pointer pr-6 py-0.5"
                >
                  {months.map(m => <option key={m.value} value={m.value} className="bg-[var(--card-bg)] text-[var(--text-dark-h)]">{m.label}</option>)}
                </select>
              </div>

              <div className="flex items-center gap-1 bg-[var(--bg-flat-50)] rounded-xl border border-[var(--border-light)] px-2.5 py-1">
                <Users className="w-3.5 h-3.5 text-[var(--text-gray)]" />
                <select 
                  value={selectedTeamId} 
                  onChange={e => setSelectedTeamId(e.target.value)}
                  className="bg-transparent border-0 text-xs font-bold text-[var(--text-dark-h)] focus:ring-0 outline-none cursor-pointer pr-6 py-0.5"
                >
                  <option value="" className="bg-[var(--card-bg)] text-[var(--text-dark-h)]">جميع الفرق</option>
                  {activeTeams.map(t => <option key={t.id} value={String(t.id)} className="bg-[var(--card-bg)] text-[var(--text-dark-h)]">{t.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {selectedTeamId ? (
            selectedTeam ? (
              <div className="space-y-4 animate-fade-in">
                <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-extrabold text-[var(--text-dark-h)]">تقرير أداء فريق: {selectedTeam.name}</h4>
                    <p className="text-xs text-[var(--text-gray)]">التقرير للفترة المحددة: {months.find(m => m.value === selectedMonth)?.label || selectedMonth}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold text-[var(--text-muted)]">نسبة إنجاز التارجت</span>
                    <p className="text-lg font-black text-blue-500">
                      {selectedTeam.monthly_target_seconds > 0 
                        ? Math.round((selectedTeam.total_seconds / selectedTeam.monthly_target_seconds) * 100) 
                        : 0}%
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-2xl border border-[var(--card-border)] bg-[var(--bg-flat-50)] flex flex-col justify-between h-28 hover:shadow-sm transition">
                    <div className="flex items-center gap-2 text-[var(--text-gray)]">
                      <Target className="w-4 h-4 text-blue-500" />
                      <span className="text-xs font-bold">التقدم والهدف</span>
                    </div>
                    <div>
                      <p className="text-[10px] text-[var(--text-muted)] mt-1">المستهدف: {formatSeconds(selectedTeam.monthly_target_seconds)}</p>
                      <p className="text-sm font-black text-[var(--text-dark-h)]">{formatSeconds(selectedTeam.total_seconds)} مُنجز</p>
                    </div>
                  </div>

                  <div className="p-4 rounded-2xl border border-[var(--card-border)] bg-[var(--bg-flat-50)] flex flex-col justify-between h-28 hover:shadow-sm transition">
                    <div className="flex items-center gap-2 text-[var(--text-gray)]">
                      <Clock className="w-4 h-4 text-sky-500" />
                      <span className="text-xs font-bold">إجمالي تسليم الدقائق</span>
                    </div>
                    <div>
                      <p className="text-sm font-black text-[var(--text-dark-h)]">
                        {Math.floor(selectedTeam.total_seconds / 60)} دقيقة و {selectedTeam.total_seconds % 60} ثانية
                      </p>
                      <p className="text-[10px] text-[var(--text-muted)]">المدة الكلية للفيديوهات</p>
                    </div>
                  </div>

                  <div className="p-4 rounded-2xl border border-[var(--card-border)] bg-[var(--bg-flat-50)] flex flex-col justify-between h-28 hover:shadow-sm transition">
                    <div className="flex items-center gap-2 text-[var(--text-gray)]">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs font-bold">المهام المنجزة</span>
                    </div>
                    <div>
                      <p className="text-sm font-black text-[var(--text-dark-h)]">{selectedTeam.task_count || 0} مهمة</p>
                      <p className="text-[10px] text-[var(--text-muted)]">إجمالي المهام المقبولة</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[var(--text-muted)] text-center py-4">البيانات غير متوفرة لهذا الفريق</p>
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-fade-in">
              {activeTeams.map((t) => {
                const percent = t.monthly_target_seconds > 0 ? Math.min(100, Math.round((t.total_seconds / t.monthly_target_seconds) * 100)) : 0;
                return (
                  <div key={t.id} className="p-4 rounded-xl border border-[var(--card-border)] bg-[var(--bg-flat-50)] space-y-2.5 shadow-sm">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-extrabold text-[var(--text-dark-h)]">{t.name}</span>
                      <span className="text-xs text-[var(--text-muted)] font-semibold">
                        {formatSeconds(t.total_seconds)} / {formatSeconds(t.monthly_target_seconds)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700/50 rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${percent >= 100 ? 'bg-gradient-to-r from-green-500 to-emerald-500' : percent >= 70 ? 'bg-gradient-to-r from-blue-500 to-indigo-500' : 'bg-gradient-to-r from-yellow-500 to-amber-500'}`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-[var(--text-muted)] font-medium pt-1">
                      <span>نسبة الإنجاز: {percent}%</span>
                      <span>إجمالي المهام المنجزة: {t.task_count || 0} مهمة</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Main Assigned Clients Cards Section */}
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 md:p-6 space-y-5">
        <div className="flex items-center justify-between border-b border-[var(--border-light)] pb-4">
          <div className="space-y-1">
            <h3 className="font-bold text-base text-[var(--text-dark-h)] flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" /> بطاقات ومتابعة العملاء المسؤول عنهم
            </h3>
            <p className="text-xs text-[var(--text-muted)]">اضغط على "سجل تواصل اليوم" لكتابة ملاحظات تواصلك الفوري وتقييم الأداء العام للعميل.</p>
          </div>
        </div>

        {(!data.clients || data.clients.length === 0) ? (
          <div className="text-center py-10 text-[var(--text-muted)] bg-[var(--bg-flat-50)] rounded-2xl text-sm">
            لا يوجد عملاء معينون لك حالياً. يرجى الطلب من المالك أو الإدارة ربط عملائك بحساب المتابعة الخاص بك.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.clients.map((client) => {
              const daysDiff = client.last_contact_date ? Math.floor((new Date() - new Date(client.last_contact_date)) / (1000 * 60 * 60 * 24)) : null;
              const isOverdue = !client.last_contact_date || daysDiff >= 7;

              return (
                <div key={client.id} className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-5 space-y-4 shadow-sm relative overflow-hidden flex flex-col justify-between hover:shadow-md transition">
                  {/* Status Indicator */}
                  <div className={`absolute top-0 left-0 right-0 h-1.5 ${isOverdue ? 'bg-red-500' : 'bg-green-500'}`} />
                  
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <span className="font-extrabold text-base text-[var(--text-dark-h)] line-clamp-1">{client.name}</span>
                      <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${isOverdue ? 'bg-red-55 text-red-700 dark:bg-red-950 dark:text-red-200' : 'bg-green-55 text-green-700 dark:bg-green-950 dark:text-green-200'}`}>
                        {isOverdue ? 'تواصل متأخر' : 'تواصل مستقر'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5 text-center mt-2.5">
                      <div className="p-2 bg-[var(--bg-flat-50)] rounded-xl border border-[var(--card-border)]">
                        <p className="text-[10px] text-[var(--text-muted)]">مهام نشطة</p>
                        <p className="text-sm font-black text-yellow-600 dark:text-yellow-400">{client.active_tasks || 0}</p>
                      </div>
                      <div className="p-2 bg-[var(--bg-flat-50)] rounded-xl border border-[var(--card-border)]">
                        <p className="text-[10px] text-[var(--text-muted)]">مهام منجزة</p>
                        <p className="text-sm font-black text-green-600 dark:text-green-400">{client.completed_tasks || 0}</p>
                      </div>
                    </div>

                    {/* Subscription Details Widget */}
                    {client.sub_id ? (
                      <div className="p-3.5 rounded-xl bg-blue-50/40 dark:bg-blue-950/10 border border-blue-100/50 dark:border-blue-950/20 text-xs space-y-2">
                        <div className="flex items-center justify-between font-bold text-blue-700 dark:text-blue-400">
                          <span>📦 باقة {client.package_name}</span>
                          <span className="text-[10px] opacity-75">ينتهي: {client.sub_period_end}</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-[10px] text-[var(--text-muted)]">
                            <span>مستهلك: {formatSeconds(client.sub_used_seconds)}</span>
                            <span>متبقي: {formatSeconds(client.sub_remaining_seconds)}</span>
                          </div>
                          {/* Progress bar */}
                          <div className="w-full h-2 bg-blue-100 dark:bg-blue-950 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full" 
                              style={{ width: `${Math.min(100, Math.round(((client.sub_used_seconds || 0) / (client.sub_total_seconds || 1)) * 100))}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 rounded-xl bg-amber-50/40 dark:bg-amber-950/10 border border-amber-100/50 dark:border-amber-950/20 text-xs text-amber-700 dark:text-amber-400 font-medium text-center">
                        ⚠️ لا يوجد باقة اشتراك نشطة
                      </div>
                    )}

                    <div className="text-xs text-[var(--text-muted)] space-y-1.5 pt-2">
                      <p className="flex justify-between">
                        <span>📞 رقم الهاتف:</span>
                        <span className="font-semibold text-[var(--text-dark-h)]">{client.phone || '—'}</span>
                      </p>
                      <p className="flex justify-between">
                        <span>🗓️ آخر تواصل:</span>
                        <span className={`font-semibold ${isOverdue ? 'text-red-600 dark:text-red-400' : 'text-[var(--text-dark-h)]'}`}>
                          {client.last_contact_date ? new Date(client.last_contact_date).toLocaleDateString('ar-IQ') : 'لم يتم التواصل'}
                        </span>
                      </p>
                    </div>

                    {/* Last Touchpoint Notes Speech Bubble */}
                    {client.last_contact_notes && (
                      <div className="p-3 rounded-xl bg-purple-50/50 dark:bg-purple-950/10 border border-purple-100/50 dark:border-purple-950/20 text-xs text-[var(--text-dark-s)] relative mt-2">
                        <p className="font-bold text-[10px] text-purple-700 dark:text-purple-400 mb-1 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> آخر ملاحظة مسجلة:
                        </p>
                        <p className="italic line-clamp-2">"{client.last_contact_notes}"</p>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-[var(--border-light)] mt-4 flex items-center justify-between gap-3">
                    <button
                      onClick={() => setActiveClient(client)}
                      className="w-full flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition shadow-sm"
                    >
                      <span>سجل تواصل اليوم ✍️</span>
                    </button>
                    <a
                      href={`/tm/tasks?client_id=${client.id}`}
                      className="px-3.5 py-2 text-xs font-bold text-[var(--text-dark-h)] bg-[var(--bg-flat-50)] hover:bg-[var(--bg-flat-100)] rounded-xl border border-[var(--card-border)] transition whitespace-nowrap"
                    >
                      المهام
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Log Touchpoint Overlay Dialog Modal */}
      {activeClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-slide-up">
            <div className="flex items-center justify-between p-4 border-b border-[var(--border-light)] bg-purple-50/50 dark:bg-purple-950/10">
              <div className="flex items-center gap-2">
                <PenTool className="w-4 h-4 text-purple-600" />
                <h3 className="font-bold text-sm text-[var(--text-dark-h)]">سجل تواصل اليوم: {activeClient.name}</h3>
              </div>
              <button
                onClick={() => setActiveClient(null)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 text-[var(--text-muted)]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleLogTouchpoint} className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">طريقة التواصل</label>
                <div className="grid grid-cols-4 gap-2 text-center text-xs">
                  {['whatsapp', 'phone', 'meeting', 'email'].map((method) => {
                    const label = { whatsapp: 'واتساب', phone: 'هاتف', meeting: 'اجتماع', email: 'بريد' }[method];
                    return (
                      <button
                        key={method}
                        type="button"
                        onClick={() => setContactType(method)}
                        className={`p-2.5 rounded-xl border font-bold transition whitespace-nowrap ${contactType === method ? 'bg-purple-600 border-purple-600 text-white shadow-sm' : 'bg-[var(--bg-flat-50)] border-[var(--card-border)] text-[var(--text-dark-s)] hover:bg-[var(--bg-flat-100)]'}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">اكتب ماذا فعلت وتناقشت معهم اليوم</label>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="اكتب تفاصيل المكالمة أو المتابعة والحلول التي ناقشتها مع العميل اليوم..."
                  className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-xs focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition resize-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">موعِد المتابعة والتواصل القادم (اختياري)</label>
                <div className="relative">
                  <input
                    type="date"
                    value={nextContactDate}
                    onChange={e => setNextContactDate(e.target.value)}
                    className="w-full p-2.5 pr-8 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-xs focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition"
                  />
                  <Calendar className="w-4 h-4 text-[var(--text-gray)] absolute right-2.5 top-3" />
                </div>
              </div>

              <div className="pt-2 border-t border-[var(--border-light)] flex gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-60 rounded-xl transition shadow-sm"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>{submitting ? 'جاري الحفظ...' : 'حفظ الملاحظة وتسجيل التواصل'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveClient(null)}
                  className="px-4 py-2.5 text-xs font-bold text-[var(--text-dark-h)] bg-[var(--bg-flat-50)] hover:bg-[var(--bg-flat-100)] rounded-xl border border-[var(--card-border)] transition"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function TeamLeaderDashboard() {
  const now = new Date();
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getMonthsList = () => {
    const list = [];
    const d = new Date();
    for (let i = 0; i < 6; i++) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const label = d.toLocaleDateString('ar-IQ', { month: 'long', year: 'numeric' });
      list.push({ value: `${year}-${month}`, label });
      d.setMonth(d.getMonth() - 1);
    }
    return list;
  };
  const months = getMonthsList();

  useEffect(() => {
    setLoading(true);
    getTmTeamLeaderDashboard(selectedMonth)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedMonth]);

  if (loading) return <div className="p-6 text-center text-[var(--text-muted)]" dir="rtl">جاري التحميل...</div>;
  if (!data || !data.team) return <div className="p-6 text-center text-[var(--text-muted)]" dir="rtl">{data?.message || 'غير متاح'}</div>;

  const prog = data.monthly_progress || {};
  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
            <Target className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--text-dark-h)]">فريق: {data.team.name}</h1>
            <p className="text-sm text-[var(--text-muted)]">{data.members?.length || 0} عضو نشط</p>
          </div>
        </div>

        {/* Monthly Filter */}
        <div className="flex items-center gap-2 bg-[var(--bg-flat-50)] rounded-xl border border-[var(--border-light)] px-3 py-1.5 w-full sm:w-auto self-start sm:self-auto">
          <Calendar className="w-4 h-4 text-[var(--text-gray)]" />
          <select 
            value={selectedMonth} 
            onChange={e => setSelectedMonth(e.target.value)}
            className="bg-transparent border-0 text-xs font-bold text-[var(--text-dark-h)] focus:ring-0 outline-none cursor-pointer pr-6 py-0.5"
          >
            {months.map(m => (
              <option key={m.value} value={m.value} className="bg-[var(--card-bg)] text-[var(--text-dark-h)]">
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="مهام نشطة" value={data.task_stats?.active || 0} icon={<Clock className="w-5 h-5" />} color="blue" />
        <StatCard title="مهام رُبحت" value={data.task_stats?.won || 0} icon={<Award className="w-5 h-5" />} color="green" />
        <StatCard title="تعديلات مطلوبة" value={data.task_stats?.revision || 0} icon={<AlertTriangle className="w-5 h-5" />} color="yellow" />
        <StatCard title="أرباح الشهر" value={formatIQD(prog.total_iqd)} icon={<DollarSign className="w-5 h-5" />} color="purple" />
      </div>

      {/* Progress Bar */}
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-sm text-[var(--text-dark-h)]">التقدم الشهري</h3>
          <span className="text-lg font-bold text-primary">{prog.progress_percentage || 0}%</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-2">
          <div className={`h-4 rounded-full transition-all ${(prog.progress_percentage || 0) >= 100 ? 'bg-green-500' : 'bg-primary'}`}
            style={{ width: `${Math.min(100, prog.progress_percentage || 0)}%` }} />
        </div>
        <p className="text-xs text-[var(--text-muted)]">{formatSeconds(prog.achieved_seconds)} من {formatSeconds(prog.target_seconds)} — {prog.task_count || 0} مهمة مقبولة</p>
      </div>

      {/* Member Performance */}
      {data.member_performance?.length > 0 && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5">
          <h3 className="font-bold text-sm text-[var(--text-dark-h)] mb-3">أداء الأعضاء</h3>
          <div className="space-y-2">
            {data.member_performance.map((m, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[var(--border-light)] last:border-0">
                <span className="text-sm font-bold text-[var(--text-dark-h)]">{m.employee_name}</span>
                <div className="flex gap-4 text-xs text-[var(--text-gray)]">
                  <span>{m.tasks} مهمة</span>
                  <span>{formatSeconds(m.seconds)}</span>
                  <span className="font-bold text-green-600 dark:text-green-400">{formatIQD(m.total_iqd)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TeamMemberDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTmTeamMemberDashboard().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-center text-[var(--text-muted)]" dir="rtl">جاري التحميل...</div>;
  if (!data) return null;

  const ts = data.task_stats || {};
  const e = data.earnings_this_month || {};
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const showFinancials = user.role !== 'team_member' || user.show_financials === 1 || user.show_financials === true || user.show_financials === '1' || localStorage.getItem('show_financials') === 'true';

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
          <Target className="w-5 h-5 text-sky-600" />
        </div>
        <h1 className="text-xl font-bold text-[var(--text-dark-h)]">لوحتي</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="مهام نشطة" value={ts.active || 0} icon={<Clock className="w-5 h-5" />} color="blue" />
        <StatCard title="مهام مكتملة" value={ts.completed || 0} icon={<CheckCircle className="w-5 h-5" />} color="green" />
        {showFinancials ? (
          <>
            <StatCard title="أرباحي هذا الشهر" value={formatIQD(e.total_iqd)} icon={<DollarSign className="w-5 h-5" />} color="purple" />
            <StatCard title="غير مدفوع" value={formatIQD(data.unpaid_total)} icon={<TrendingUp className="w-5 h-5" />} color="red" />
          </>
        ) : (
          <>
            <StatCard title="إجمالي ثواني الشهر" value={formatSeconds(e.total_seconds || 0)} icon={<Clock className="w-5 h-5" />} color="purple" />
            <StatCard title="إجمالي المهام المنجزة" value={`${e.task_count || 0} مهمة`} icon={<Award className="w-5 h-5" />} color="red" />
          </>
        )}
      </div>

      {/* My Tasks */}
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5">
        <h3 className="font-bold text-sm text-[var(--text-dark-h)] mb-3">مهامي</h3>
        {(data.tasks || []).length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">لا توجد مهام معيّنة إليك</p>
        ) : (
          <div className="space-y-2">
            {data.tasks.map(t => (
              <div key={t.id} className="flex items-center justify-between py-2 border-b border-[var(--border-light)] last:border-0">
                <div>
                  <p className="text-sm font-bold text-[var(--text-dark-h)]">{t.title}</p>
                  <p className="text-xs text-[var(--text-muted)]">{t.client_name} — {t.team_name}</p>
                </div>
                <div className="text-left">
                  <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${TM_STATUS_COLORS[t.status] || ''}`}>
                    {TM_STATUS_MAP[t.status]?.label || t.status}
                  </span>
                  <p className="text-xs text-[var(--text-muted)] mt-1">{formatSeconds(t.video_seconds)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle, icon, color }) {
  const colors = {
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 ring-green-200 dark:ring-green-900/30',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 ring-blue-200 dark:ring-blue-900/30',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 ring-purple-200 dark:ring-purple-900/30',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 ring-yellow-200 dark:ring-yellow-900/30',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 ring-red-200 dark:ring-red-900/30',
  };

  return (
    <div className={`p-4 rounded-2xl ring-1 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-2 opacity-70">{icon}<span className="text-xs font-bold">{title}</span></div>
      <p className="text-xl font-bold">{value}</p>
      {subtitle && <p className="text-[10px] opacity-60 mt-0.5">{subtitle}</p>}
    </div>
  );
}

function AlertCard({ icon, title, value, color }) {
  const colors = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
  };
  return (
    <div className={`p-4 rounded-2xl flex items-center gap-3 ${colors[color]}`}>
      {icon}
      <div>
        <p className="text-sm font-bold">{value}</p>
        <p className="text-xs opacity-70">{title}</p>
      </div>
    </div>
  );
}

function DelayedTasksPanel({ tasks }) {
  if (!tasks || tasks.length === 0) return null;

  return (
    <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-2xl p-5 space-y-3">
      <h3 className="font-bold text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-red-600" /> 🚨 تحذير: مهام متأخرة عن موعد التسليم ({tasks.length})
      </h3>
      <p className="text-xs text-red-600 dark:text-red-400">تجاوزت هذه المهام الموعد النهائي المحدد للتسليم من قبل المشرفين دون أن يتم قبولها.</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
        {tasks.map((task) => (
          <div key={task.id} className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-red-200/60 dark:border-red-950/30 shadow-sm relative overflow-hidden flex flex-col justify-between">
            <div className="absolute right-0 top-0 bottom-0 w-1.5 bg-red-500" />
            <div className="pr-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <span className="font-bold text-sm text-[var(--text-dark-h)] line-clamp-1">{task.title}</span>
                <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200">
                  متأخرة بـ {task.days_overdue} {task.days_overdue === 1 ? 'يوم' : 'أيام'}
                </span>
              </div>
              
              <div className="text-xs text-[var(--text-muted)] space-y-1">
                <p>💼 العميل: <span className="font-semibold text-[var(--text-dark-h)]">{task.display_client_name}</span></p>
                <p>👥 الفريق المسؤول: <span className="font-semibold text-[var(--text-dark-h)]">{task.assigned_teams || 'غير معين'}</span></p>
                {task.leaders && <p>👑 قائد الفريق: <span className="font-semibold text-blue-600 dark:text-blue-400">{task.leaders}</span></p>}
              </div>
            </div>

            <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-zinc-800 flex items-center justify-between text-xs">
              <span className="text-[10px] font-semibold text-gray-500">الموعد المطلوب: {task.deadline}</span>
              <a href={`/tm/tasks?id=${task.id}`} className="text-red-600 dark:text-red-400 font-bold hover:underline">متابعة المهمة ←</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
